import {
  BarChart3,
  Building2,
  ClipboardList,
  FileText,
  LayoutDashboard,
  Mail,
  Settings,
  Target,
  Users,
} from 'lucide-react';

export const ROLE_HOME_PATHS = Object.freeze({
  agent: '/agent',
  team_leader: '/team-leader',
  manager: '/manager',
  director: '/manager',
  admin: '/admin',
  client: '/client',
});

export const ROLE_REPORT_PATHS = Object.freeze({
  agent: '/agent/reports',
  team_leader: '/team-leader/reports',
  manager: '/manager/reports',
  director: '/manager/reports',
  admin: '/admin/reports',
  client: '/client/reports',
});

const INTERNAL_ROLES = ['agent', 'team_leader', 'manager', 'director', 'admin'];

export const ROLE_CAPABILITIES = Object.freeze({
  agent: Object.freeze(['dashboard', 'reports', 'mail', 'insights', 'eod']),
  team_leader: Object.freeze(['dashboard', 'reports', 'mail', 'insights', 'eod']),
  manager: Object.freeze(['dashboard', 'reports', 'processing', 'mail', 'insights']),
  director: Object.freeze([
    'dashboard',
    'reports',
    'processing',
    'mail',
    'insights',
    'documents',
    'administration',
  ]),
  admin: Object.freeze([
    'dashboard',
    'reports',
    'mail',
    'insights',
    'documents',
    'administration',
  ]),
  client: Object.freeze(['dashboard', 'reports']),
});

export function getRoleHomePath(role) {
  return ROLE_HOME_PATHS[role] || '/';
}

export function getRoleReportPath(role) {
  return ROLE_REPORT_PATHS[role] || getRoleHomePath(role);
}

export function hasCapability(userOrRole, capability) {
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole?.role;
  return Boolean(role && ROLE_CAPABILITIES[role]?.includes(capability));
}

export function isInternalRole(userOrRole) {
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole?.role;
  return INTERNAL_ROLES.includes(role);
}

/**
 * Canonical role-aware navigation. Keep route permissions in App.js aligned
 * with this list. `testId` values preserve the existing browser-test contract.
 */
export function getNavigationItems(user) {
  const role = user?.role;
  if (!role || !ROLE_HOME_PATHS[role]) return [];

  const items = [
    {
      path: getRoleHomePath(role),
      icon: LayoutDashboard,
      label: 'Dashboard',
      testId: 'nav-dashboard',
      section: 'Workspace',
    },
    {
      path: getRoleReportPath(role),
      icon: BarChart3,
      label: 'Reports',
      testId: 'nav-reports',
      section: 'Workspace',
    },
  ];

  if (role === 'client') {
    items.push({
      path: '/client/descriptors',
      icon: Settings,
      label: 'Descriptors',
      testId: 'nav-descriptors',
      section: 'Workspace',
    });
  }

  if (hasCapability(role, 'processing')) {
    items.push({
      path: '/manager/pending-sales',
      icon: ClipboardList,
      label: 'Pending Sales',
      testId: 'nav-pending-sales',
      badge: 'pending',
      section: 'Workspace',
    });
  }

  if (hasCapability(role, 'documents')) {
    items.push({
      path: '/director/documents',
      icon: FileText,
      label: 'Documents',
      testId: 'nav-documents',
      section: 'Workspace',
    });
  }

  if (hasCapability(role, 'administration')) {
    items.push(
      {
        path: '/admin/users',
        icon: Users,
        label: 'Users',
        testId: 'nav-users',
        section: 'Administration',
      },
      {
        path: '/admin/targets',
        icon: Target,
        label: 'Targets',
        testId: 'nav-targets',
        section: 'Administration',
      },
      {
        path: '/admin/descriptors',
        icon: Settings,
        label: 'Descriptors',
        testId: 'nav-descriptors',
        section: 'Administration',
      },
      {
        path: '/admin/centers',
        icon: Building2,
        label: 'Centers',
        testId: 'nav-centers',
        section: 'Administration',
      },
    );
  }

  if (hasCapability(role, 'mail')) {
    items.push({
      path: '/mail',
      icon: Mail,
      label: 'Mail',
      testId: 'nav-mail',
      badge: 'mail',
      section: 'Communication',
    });
  }

  return items;
}

export function groupNavigationItems(items) {
  return items.reduce((groups, item) => {
    const section = item.section || 'Workspace';
    const current = groups.find((group) => group.label === section);
    if (current) current.items.push(item);
    else groups.push({ label: section, items: [item] });
    return groups;
  }, []);
}

export function isNavigationItemActive(pathname, itemPath) {
  if (!pathname || !itemPath) return false;
  return pathname === itemPath;
}
